import { Component } from '@angular/core';
import { AppMaterialModule } from '../app-modules/app-material.module';
import { UtilModule } from '../app-modules/util.module';
import { TutorialVideoService } from '../services/tutorial-video.service';
import { NotificationService } from '../services/notification.service';

interface AdminHelpResourceItem {
  title: string;
  description: string;
  icon: string;
  metaItems: { icon: string; label: string }[];
  screenId?: string;
  resourceType: 'document' | 'video';
  action: () => void;
}

@Component({
  selector: 'app-admin-help-support',
  standalone: true,
  imports: [AppMaterialModule, UtilModule],
  templateUrl: './admin-help-support.component.html',
  styleUrl: './admin-help-support.component.css'
})
export class AdminHelpSupportComponent {
  openingVideoScreenId = '';

  readonly documentResources: AdminHelpResourceItem[] = [
    {
      title: 'User manual',
      description: 'Step-by-step guide to navigating the application.',
      icon: 'menu_book',
      metaItems: [
        { icon: 'slideshow', label: 'PPTX' },
        { icon: 'auto_stories', label: '48 pages' }
      ],
      resourceType: 'document',
      action: () => this.downloadAsset('assets/CERT_User_manual_V1.0.pptx', 'CERT_User_manual_V1.0.pptx')
    },
    {
      title: 'Process flow',
      description: 'See how request moves from submission to approval.',
      icon: 'account_tree',
      metaItems: [
        { icon: 'picture_as_pdf', label: 'PDF' },
        { icon: 'auto_stories', label: '1 page' }
      ],
      resourceType: 'document',
      action: () => this.downloadAsset('assets/CERT_Process_Flow.pdf', 'CERT_Process_Flow.pdf')
    }
  ];

  readonly videoResources: AdminHelpResourceItem[] = [
    {
      title: 'Admin dashboard',
      description: 'Overview of the Admin Dashboard and project tracking.',
      icon: 'dashboard',
      metaItems: [
        { icon: 'timer', label: '3:19 mins' }
      ],
      screenId: 'admin-dashboard-screen',
      resourceType: 'video',
      action: () => this.openTutorialVideo('admin-dashboard-screen')
    },
    {
      title: 'Admin detailed screen',
      description: 'Review project details, status, and document activity in one place.',
      icon: 'fact_check',
      metaItems: [
        { icon: 'timer', label: '5:05 mins' }
      ],
      screenId: 'dashboard-detailed-screen',
      resourceType: 'video',
      action: () => this.openTutorialVideo('dashboard-detailed-screen')
    },
    {
      title: 'Create collection',
      description: 'Create a collection and raise a new compliance request.',
      icon: 'add_circle_outline',
      metaItems: [
        { icon: 'timer', label: '4:59 mins' }
      ],
      screenId: 'create-collection',
      resourceType: 'video',
      action: () => this.openTutorialVideo('create-collection')
    },
    {
      title: 'Copy collection',
      description: 'Copy an existing collection request to create a new request.',
      icon: 'content_copy',
      metaItems: [
        { icon: 'timer', label: '1:46 mins' }
      ],
      screenId: 'copy-collection',
      resourceType: 'video',
      action: () => this.openTutorialVideo('copy-collection')
    },
    {
      title: 'Manage project',
      description: 'Review all requests and check their current status.',
      icon: 'work_outline',
      metaItems: [
        { icon: 'timer', label: '2:00 mins' }
      ],
      screenId: 'manage-project',
      resourceType: 'video',
      action: () => this.openTutorialVideo('manage-project')
    },
    {
      title: 'Manage documents',
      description: 'Maintain the document master used across collections.',
      icon: 'attach_file',
      metaItems: [
        { icon: 'timer', label: '2:17 mins' }
      ],
      screenId: 'manage-documents',
      resourceType: 'video',
      action: () => this.openTutorialVideo('manage-documents')
    },
    {
      title: 'Manage collections',
      description: 'Maintain collections and sub-collections configuration.',
      icon: 'settings',
      metaItems: [
        { icon: 'timer', label: '4:03 mins' }
      ],
      screenId: 'manage-collections',
      resourceType: 'video',
      action: () => this.openTutorialVideo('manage-collections')
    },
    {
      title: 'Manage custom department',
      description: 'Create and manage custom departments and their team members.',
      icon: 'apartment',
      metaItems: [
        { icon: 'timer', label: '3:08 mins' }
      ],
      screenId: 'manage-custom-department',
      resourceType: 'video',
      action: () => this.openTutorialVideo('manage-custom-department')
    }
  ];

  readonly helpResources: AdminHelpResourceItem[] = [
    ...this.documentResources,
    ...this.videoResources,
  ];

  constructor(
    private readonly tutorialVideoService: TutorialVideoService,
    private readonly notificationService: NotificationService
  ) {}

  openHelpResource(resource: AdminHelpResourceItem): void {
    resource.action();
  }

  isOpeningVideo(screenId: string): boolean {
    return this.openingVideoScreenId === screenId;
  }

  private openTutorialVideo(screenId: string): void {
    if (this.openingVideoScreenId) {
      return;
    }

    const popup = window.open('about:blank', '_blank');
    if (popup) {
      popup.document.title = 'Opening video walkthrough...';
      const message = popup.document.createElement('p');
      message.style.fontFamily = 'Arial, sans-serif';
      message.style.padding = '16px';
      message.textContent = 'Opening video walkthrough...';
      popup.document.body.appendChild(message);
      popup.document.close();

      try {
        popup.opener = null;
      } catch {
        // Ignore browsers that do not allow assigning opener.
      }
    }

    this.openingVideoScreenId = screenId;

    this.tutorialVideoService.getTutorialVideoUrl(screenId).subscribe({
      next: (resource) => {
        this.openingVideoScreenId = '';

        if (!resource.signedUrl) {
          popup?.close();
          this.notificationService.showError('Tutorial video is not available right now.');
          return;
        }

        if (popup && !popup.closed) {
          popup.location.replace(resource.signedUrl);
        } else {
          window.open(resource.signedUrl, '_blank', 'noopener,noreferrer');
        }
      },
      error: () => {
        this.openingVideoScreenId = '';
        popup?.close();
        this.notificationService.showError('Unable to open the tutorial video. Please try again.');
      }
    });
  }

  private downloadAsset(path: string, fileName: string): void {
    const anchor = document.createElement('a');
    anchor.href = path;
    anchor.download = fileName;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
}
